import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, ShoppingBag, X as XIcon, ChevronRight } from 'lucide-react';
import api, { toPage } from '../services/api';
import {
  PageHeader,
  Card,
  Segmented,
  SearchInput,
  Pagination,
  Sheet,
  Button,
  Badge,
  Field,
  OrderStatusBadge,
  PlatformBadge,
  ORDER_PLATFORM_TABS,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  ResponsiveList,
  inputClass,
  useDebounce
} from '../components/ui';
import { formatCurrency, formatDate, fromUnix, sumItemCommission } from '../utils/format';

const STATUS_TABS = [
  { value: '', label: 'Tất cả' },
  { value: 'PENDING', label: 'Chờ xử lý' },
  { value: 'COMPLETED', label: 'Hoàn thành' },
  { value: 'CANCELLED', label: 'Đã hủy' }
];

const OrdersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFilter = searchParams.get('user_id') || '';

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [platform, setPlatform] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const debouncedSearch = useDebounce(search);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['orders', debouncedSearch, status, platform, dateFrom, dateTo, userIdFilter, page, limit],
    async () => {
      const params = { page, limit };
      if (debouncedSearch) params.search = debouncedSearch;
      if (status) params.status = status;
      if (platform) params.platform = platform;
      if (dateFrom) params.start_date = dateFrom;
      if (dateTo) params.end_date = dateTo;
      if (userIdFilter) params.user_id = userIdFilter;
      return toPage(await api.get('/admin/orders', { params }));
    },
    { keepPreviousData: true }
  );

  const orders = data?.items || [];
  const pagination = data?.pagination || {};
  const hasDateFilter = !!(dateFrom || dateTo);

  const clearUser = () => {
    searchParams.delete('user_id');
    setSearchParams(searchParams);
    setPage(1);
  };

  const openOrder = (o) => navigate(`/orders/${o.id}`);

  const renderCard = (o) => (
    <Card as="button" type="button" onClick={() => openOrder(o)} className="w-full p-4 text-left active:bg-gray-50">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-mono text-sm font-semibold text-gray-900">{o.order_sn}</p>
          <p className="truncate text-xs text-gray-500">{o.user?.name || '—'} · {o.user?.email || 'Không rõ user'}</p>
        </div>
        <div className="flex flex-shrink-0 gap-1">
          <PlatformBadge platform={o.platform} />
          <OrderStatusBadge status={o.orderStatus} />
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <div className="text-xs text-gray-500">
          <p>{o.items?.length || 0} sản phẩm · tạo {formatDate(o.created_at)}</p>
          {o.complete_time > 0 && <p>Hoàn thành {formatDate(fromUnix(o.complete_time))}</p>}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-base font-semibold text-gray-900">{formatCurrency(sumItemCommission(o.items))}</span>
          <ChevronRight className="h-4 w-4 text-gray-300" />
        </div>
      </div>
    </Card>
  );

  const columns = [
    { key: 'sn', title: 'Mã đơn', render: (o) => <span className="font-mono font-medium text-gray-900">{o.order_sn}</span> },
    {
      key: 'user',
      title: 'Người dùng',
      render: (o) => (
        <div>
          <p className="font-medium text-gray-900">{o.user?.name || '—'}</p>
          <p className="text-xs text-gray-500">{o.user?.email || '—'}</p>
        </div>
      )
    },
    { key: 'platform', title: 'Sàn', render: (o) => <PlatformBadge platform={o.platform} /> },
    { key: 'status', title: 'Trạng thái', render: (o) => <OrderStatusBadge status={o.orderStatus} /> },
    { key: 'items', title: 'SP', align: 'right', render: (o) => o.items?.length || 0 },
    { key: 'cb', title: 'Hoàn tiền', align: 'right', render: (o) => <span className="font-semibold">{formatCurrency(sumItemCommission(o.items))}</span> },
    { key: 'created', title: 'Ngày tạo', render: (o) => formatDate(o.created_at) },
    { key: 'done', title: 'Hoàn thành', render: (o) => (o.complete_time > 0 ? formatDate(fromUnix(o.complete_time)) : '—') }
  ];

  return (
    <div>
      <PageHeader title="Đơn hàng" subtitle={`${pagination.total ?? 0} đơn`} />

      <div className="filter-bar">
        <div className="flex gap-2 lg:min-w-[18rem] lg:flex-1">
          <SearchInput className="flex-1" value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Mã đơn hoặc email" />
          <Button variant={hasDateFilter ? 'soft' : 'outline'} size="icon" className="h-11 w-11 flex-shrink-0 md:h-9 md:w-9" onClick={() => setShowFilters(true)} aria-label="Bộ lọc">
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>
        <Segmented options={ORDER_PLATFORM_TABS} value={platform} onChange={(v) => { setPlatform(v); setPage(1); }} />
        <Segmented options={STATUS_TABS} value={status} onChange={(v) => { setStatus(v); setPage(1); }} />
        {(userIdFilter || hasDateFilter) && (
          <div className="flex flex-wrap gap-2">
            {userIdFilter && (
              <Badge className="gap-1 py-1">
                User: {userIdFilter.slice(0, 8)}…
                <button type="button" onClick={clearUser} aria-label="Bỏ lọc user"><XIcon className="h-3.5 w-3.5" /></button>
              </Badge>
            )}
            {hasDateFilter && (
              <Badge className="gap-1 py-1">
                {dateFrom || '…'} → {dateTo || '…'}
                <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }} aria-label="Bỏ lọc ngày"><XIcon className="h-3.5 w-3.5" /></button>
              </Badge>
            )}
          </div>
        )}
      </div>

      <ErrorBanner message={error ? 'Lỗi khi tải danh sách đơn hàng' : ''} onRetry={refetch} />

      {isLoading ? (
        <SkeletonList />
      ) : (
        <ResponsiveList
          items={orders}
          renderCard={renderCard}
          columns={columns}
          onRowClick={openOrder}
          empty={<EmptyState icon={ShoppingBag} title="Không có đơn hàng" description="Thử đổi bộ lọc hoặc từ khóa." />}
        />
      )}

      {pagination.total > 0 && (
        <Pagination
          page={page}
          totalPages={pagination.pages}
          total={pagination.total}
          onChange={setPage}
          limit={limit}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
          disabled={isFetching}
        />
      )}

      <Sheet
        open={showFilters}
        onClose={() => setShowFilters(false)}
        title="Lọc theo ngày tạo"
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); setShowFilters(false); }}>Xóa lọc</Button>
            <Button onClick={() => { setPage(1); setShowFilters(false); }}>Áp dụng</Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Từ ngày">
            <input type="date" className={inputClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field label="Đến ngày">
            <input type="date" className={inputClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
        </div>
      </Sheet>
    </div>
  );
};

export default OrdersPage;
